/**
 * Session Cache Management
 *
 * Manages client-side session cache using sessionStorage instead of localStorage.
 * Stores user/org data needed for app functionality while excluding truly sensitive fields.
 *
 * Security improvements over localStorage:
 * - sessionStorage clears on tab close (much shorter lifetime)
 * - Not shared across tabs (limited exposure)
 * - Excludes passwords, tokens, API keys, billing info
 * - Combined with XSS fixes, provides strong security
 *
 * Data exclusions:
 * - password, passwordHash
 * - tokens, apiKeys
 * - stripeCustomerId
 * - OAuth credentials
 *
 * @module utils/sessionCache
 */

const SESSION_KEYS = {
  USER_CACHE: 'user_minimal',
  ORG_CACHE: 'org_minimal',
  UI_PREFS: 'ui_preferences',
  LAST_ROUTE: 'last_route'
};

/**
 * Extracts only non-sensitive data for client-side cache
 *
 * @param {object} fullUser - Complete user object from server
 * @returns {object|null} - Minimal user data safe for client cache
 */
export const getMinimalUserData = (fullUser) => {
  if (!fullUser) return null;

  // Security strategy: Store full user object in sessionStorage EXCEPT truly sensitive fields
  // sessionStorage is much more secure than localStorage because:
  // 1. It clears when tab/browser closes (short lifetime)
  // 2. It's not shared across tabs (limited exposure)
  // 3. Combined with XSS fixes, risk is minimal
  //
  // We exclude only truly sensitive data that should never be client-side:
  const { password, passwordHash, tokens, apiKeys, stripeCustomerId, ...safeUserData } = fullUser;

  return safeUserData;
};

/**
 * Extracts minimal organization data for cache
 *
 * @param {array} orgs - Full organizations array
 * @returns {array} - Minimal org data
 */
export const getMinimalOrgData = (orgs) => {
  if (!Array.isArray(orgs)) return [];

  // Store full org data in sessionStorage (clears on tab close)
  // Exclude only truly sensitive fields like billing tokens
  return orgs.map(org => {
    const { stripeCustomerId, apiKeys, tokens, ...safeOrgData } = org;
    return safeOrgData;
  });
};

/**
 * Store minimal user data in sessionStorage
 * sessionStorage clears on tab close (more secure than localStorage)
 *
 * @param {object} userData - User data to cache
 */
export const cacheUserData = (userData) => {
  if (!userData) {
    sessionStorage.removeItem(SESSION_KEYS.USER_CACHE);
    return;
  }

  try {
    const minimal = getMinimalUserData(userData);
    sessionStorage.setItem(SESSION_KEYS.USER_CACHE, JSON.stringify(minimal));
  } catch (error) {
    console.error('Error caching user data:', error);
    // If storage is full or unavailable, fail gracefully
  }
};

/**
 * Retrieve cached user data
 *
 * @returns {object|null} - Cached user data or null
 */
export const getCachedUserData = () => {
  try {
    const cached = sessionStorage.getItem(SESSION_KEYS.USER_CACHE);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading cached user data:', error);
    return null;
  }
};

/**
 * Cache organization data
 *
 * @param {array} orgData - Organizations to cache
 */
export const cacheOrgData = (orgData) => {
  if (!orgData) {
    sessionStorage.removeItem(SESSION_KEYS.ORG_CACHE);
    return;
  }

  try {
    const minimal = getMinimalOrgData(orgData);
    sessionStorage.setItem(SESSION_KEYS.ORG_CACHE, JSON.stringify(minimal));
  } catch (error) {
    console.error('Error caching org data:', error);
  }
};

/**
 * Get cached organization data
 *
 * @returns {array} - Cached organizations
 */
export const getCachedOrgData = () => {
  try {
    const cached = sessionStorage.getItem(SESSION_KEYS.ORG_CACHE);
    return cached ? JSON.parse(cached) : [];
  } catch (error) {
    console.error('Error reading cached org data:', error);
    return [];
  }
};

/**
 * Clear all cached data (on logout)
 * Also cleans up legacy localStorage keys
 */
export const clearCache = () => {
  // Clear sessionStorage
  Object.values(SESSION_KEYS).forEach(key => {
    sessionStorage.removeItem(key);
  });

  // Clean up legacy localStorage keys (migration)
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('orgs');
    localStorage.removeItem('syncState');
  } catch (error) {
    // Ignore errors if localStorage is not available
  }
};

/**
 * One-time migration from localStorage to sessionStorage
 * Runs on app load to clean up legacy data
 *
 * @returns {boolean} - True if migration was performed
 */
export const migrateFromLocalStorage = () => {
  try {
    let migrated = false;

    // Check if old data exists in localStorage
    const oldUser = localStorage.getItem('user');
    const oldOrgs = localStorage.getItem('orgs');

    if (oldUser) {
      // Parse and cache minimal data in sessionStorage
      try {
        const userData = JSON.parse(oldUser);
        cacheUserData(userData);
        migrated = true;
      } catch (e) {
        console.error('Error migrating user data:', e);
      }

      // Remove old data
      localStorage.removeItem('user');
    }

    if (oldOrgs) {
      try {
        const orgData = JSON.parse(oldOrgs);
        cacheOrgData(orgData);
        migrated = true;
      } catch (e) {
        console.error('Error migrating org data:', e);
      }

      // Remove old data
      localStorage.removeItem('orgs');
    }

    // Remove any other legacy keys
    localStorage.removeItem('syncState');

    return migrated;
  } catch (error) {
    console.error('Migration error:', error);
    // Clear everything on error to be safe
    clearCache();
    return false;
  }
};

/**
 * Save UI preferences (non-sensitive settings)
 *
 * @param {object} preferences - UI preferences to save
 */
export const saveUIPreferences = (preferences) => {
  try {
    sessionStorage.setItem(SESSION_KEYS.UI_PREFS, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error saving UI preferences:', error);
  }
};

/**
 * Get UI preferences
 *
 * @returns {object} - UI preferences
 */
export const getUIPreferences = () => {
  try {
    const prefs = sessionStorage.getItem(SESSION_KEYS.UI_PREFS);
    return prefs ? JSON.parse(prefs) : {};
  } catch (error) {
    console.error('Error reading UI preferences:', error);
    return {};
  }
};

/**
 * Save last route for redirect after login
 *
 * @param {string} route - Route path
 */
export const saveLastRoute = (route) => {
  try {
    sessionStorage.setItem(SESSION_KEYS.LAST_ROUTE, route);
  } catch (error) {
    console.error('Error saving last route:', error);
  }
};

/**
 * Get last saved route
 *
 * @returns {string|null} - Last route or null
 */
export const getLastRoute = () => {
  try {
    return sessionStorage.getItem(SESSION_KEYS.LAST_ROUTE);
  } catch (error) {
    console.error('Error reading last route:', error);
    return null;
  }
};

/**
 * Get storage info for debugging
 *
 * @returns {object} - Storage usage info
 */
export const getStorageInfo = () => {
  const info = {
    sessionStorage: {
      available: typeof sessionStorage !== 'undefined',
      keys: [],
    },
    localStorage: {
      available: typeof localStorage !== 'undefined',
      legacyKeys: [],
    }
  };

  try {
    if (info.sessionStorage.available) {
      info.sessionStorage.keys = Object.keys(sessionStorage);
    }
    if (info.localStorage.available) {
      // Check for legacy keys that should have been migrated
      const legacyKeys = ['user', 'orgs', 'syncState'];
      info.localStorage.legacyKeys = legacyKeys.filter(key =>
        localStorage.getItem(key) !== null
      );
    }
  } catch (error) {
    console.error('Error getting storage info:', error);
  }

  return info;
};

export default {
  cacheUserData,
  getCachedUserData,
  cacheOrgData,
  getCachedOrgData,
  clearCache,
  migrateFromLocalStorage,
  saveUIPreferences,
  getUIPreferences,
  saveLastRoute,
  getLastRoute,
  getStorageInfo,
};
